
import React from 'react';
import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { useForm } from 'react-hook-form';
import { PlacePhotos } from './PlacePhotos';
import { PlaceBasicInfo } from './PlaceBasicInfo';
import { PlaceDetails } from './PlaceDetails';
import GooglePlacesSearch from '../GooglePlacesSearch';
import { PlaceFormActions } from './PlaceFormActions';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthSession } from '@/hooks/use-auth-session';

export function PlaceFormContainer({ 
  placeFormState, 
  playlist, 
  onClose, 
  handlePlaceSelect, 
  isExistingPlace = false 
}) {
  const { 
    name, photos, photoInput, handleSubmit, setPhotoInput, setPhotos
  } = placeFormState;

  const form = useForm();
  const session = useAuthSession();

  const { data: isAdmin } = useQuery({
    queryKey: ['is-admin', session?.user?.id],
    enabled: !!session?.user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('is_admin', { user_id: session?.user?.id });
      
      if (error) throw error;
      return !!data;
    }
  });
  
  // Function to handle form submission
  const onSubmitForm = (e) => {
    e.preventDefault();
    handleSubmit();
  };

  return (
    <Form {...form}>
      <form onSubmit={onSubmitForm}>
        <div className="space-y-6">
          {isAdmin && (
            <div className="space-y-4 py-4 border-b border-gray-100">
              <h3 className="text-lg font-medium">Search for a place (Admin Only)</h3>
              <p className="text-sm text-muted-foreground mb-4">
                You can search for a place to auto-fill details, or add a place manually below.
              </p>
              
              <GooglePlacesSearch 
                onPlaceSelect={handlePlaceSelect} 
              />
            </div>
          )}

          <div className="space-y-6 py-4">
            <PlaceBasicInfo 
              name={placeFormState.name}
              setName={placeFormState.setName}
              placeType={placeFormState.placeType}
              setPlaceType={placeFormState.setPlaceType}
              rating={placeFormState.rating}
              setRating={placeFormState.setRating}
              priceRange={placeFormState.priceRange}
              setPriceRange={placeFormState.setPriceRange}
              isExistingPlace={isExistingPlace}
            />
            
            <PlacePhotos
              photos={photos}
              setPhotos={setPhotos}
              photoInput={photoInput}
              setPhotoInput={setPhotoInput}
              isExistingPlace={isExistingPlace}
              placeName={name || "Place"}
            />
            
            <PlaceDetails 
              placeType={placeFormState.placeType}
              note={placeFormState.note}
              setNote={placeFormState.setNote}
              url={placeFormState.url}
              setUrl={placeFormState.setUrl}
              requiresReservation={placeFormState.requiresReservation}
              setRequiresReservation={placeFormState.setRequiresReservation}
              cuisineType={placeFormState.cuisineType}
              setCuisineType={placeFormState.setCuisineType}
              amenities={placeFormState.amenities}
              setAmenities={placeFormState.setAmenities}
              accessibilityFeatures={placeFormState.accessibilityFeatures}
              setAccessibilityFeatures={placeFormState.setAccessibilityFeatures}
              bestTimeToVisit={placeFormState.bestTimeToVisit}
              setBestTimeToVisit={placeFormState.setBestTimeToVisit}
              tags={placeFormState.tags}
              setTags={placeFormState.setTags}
              address={placeFormState.address}
              setAddress={placeFormState.setAddress}
              
              // Restaurant-specific props
              mealPriceRange={placeFormState.mealPriceRange}
              setMealPriceRange={placeFormState.setMealPriceRange}
              cuisineSpecialties={placeFormState.cuisineSpecialties}
              setCuisineSpecialties={placeFormState.setCuisineSpecialties}
              dressCode={placeFormState.dressCode}
              setDressCode={placeFormState.setDressCode}
              outdoorSeating={placeFormState.outdoorSeating}
              setOutdoorSeating={placeFormState.setOutdoorSeating}
              
              // Hotel-specific props
              hotelClass={placeFormState.hotelClass}
              setHotelClass={placeFormState.setHotelClass}
              checkInTime={placeFormState.checkInTime}
              setCheckInTime={placeFormState.setCheckInTime}
              checkOutTime={placeFormState.checkOutTime}
              setCheckOutTime={placeFormState.setCheckOutTime}
              hasPool={placeFormState.hasPool}
              setHasPool={placeFormState.setHasPool}
              hasSpa={placeFormState.hasSpa}
              setHasSpa={placeFormState.setHasSpa}
              hasFitnessCenter={placeFormState.hasFitnessCenter}
              setHasFitnessCenter={placeFormState.setHasFitnessCenter}
              hasRoomService={placeFormState.hasRoomService}
              setHasRoomService={placeFormState.setHasRoomService}
              
              // Activity-specific props
              difficultyLevel={placeFormState.difficultyLevel}
              setDifficultyLevel={placeFormState.setDifficultyLevel}
              duration={placeFormState.duration}
              setDuration={placeFormState.setDuration}
              equipmentNeeded={placeFormState.equipmentNeeded}
              setEquipmentNeeded={placeFormState.setEquipmentNeeded}
              maxGroupSize={placeFormState.maxGroupSize}
              setMaxGroupSize={placeFormState.setMaxGroupSize}
              minAge={placeFormState.minAge}
              setMinAge={placeFormState.setMinAge}
              suitableForChildren={placeFormState.suitableForChildren}
              setSuitableForChildren={placeFormState.setSuitableForChildren}
              weatherDependent={placeFormState.weatherDependent}
              setWeatherDependent={placeFormState.setWeatherDependent}
              
              // Festival/Event-specific props
              startDate={placeFormState.startDate}
              setStartDate={placeFormState.setStartDate}
              endDate={placeFormState.endDate}
              setEndDate={placeFormState.setEndDate}
              frequency={placeFormState.frequency}
              setFrequency={placeFormState.setFrequency}
              ticketPrice={placeFormState.ticketPrice}
              setTicketPrice={placeFormState.setTicketPrice}
              categories={placeFormState.categories}
              setCategories={placeFormState.setCategories}
              capacity={placeFormState.capacity}
              setCapacity={placeFormState.setCapacity}
              familyFriendly={placeFormState.familyFriendly}
              setFamilyFriendly={placeFormState.setFamilyFriendly}
              hasFood={placeFormState.hasFood}
              setHasFood={placeFormState.setHasFood}
              
              // Attraction-specific props
              entranceFee={placeFormState.entranceFee}
              setEntranceFee={placeFormState.setEntranceFee}
              attractionType={placeFormState.attractionType}
              setAttractionType={placeFormState.setAttractionType}
              suggestedDuration={placeFormState.suggestedDuration}
              setSuggestedDuration={placeFormState.setSuggestedDuration}
              exhibits={placeFormState.exhibits}
              setExhibits={placeFormState.setExhibits}
              historicalSignificance={placeFormState.historicalSignificance}
              setHistoricalSignificance={placeFormState.setHistoricalSignificance}
              isIndoor={placeFormState.isIndoor}
              setIsIndoor={placeFormState.setIsIndoor}
              
              isExistingPlace={isExistingPlace}
            />
            
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">Save Place</Button>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
}
